import React, { useLayoutEffect } from 'react';
import { Tabs, TabList } from '@wegroup/design-system';
import { useTranslation } from 'react-i18next';
import { LanguageNew } from '../../../../types/Common';
import LanguageTab from './LanguageTab';

interface TabType {
  label: string;
  value: LanguageNew;
}

interface Props {
  value: LanguageNew;
  onChange: (lang: LanguageNew) => void;
  completedTabs: LanguageNew[];
  customTabs?: TabType[];
  isFitted?: boolean;
  disabledTabs?: LanguageNew[];
  disabledTooltip?: string;
}

const LanguageTabs: React.FC<Props> = ({
  value,
  onChange,
  completedTabs,
  customTabs,
  isFitted = true,
  disabledTabs = [],
  disabledTooltip,
}) => {
  const { t } = useTranslation();
  const defaultTabs: TabType[] = [
    {
      label: t('English'),
      value: 'en' as LanguageNew,
    },
    {
      label: t('Dutch'),
      value: 'nl' as LanguageNew,
    },
    {
      label: t('French'),
      value: 'fr' as LanguageNew,
    },
    {
      label: t('GERMAN'),
      value: 'de' as LanguageNew,
    },
  ];
  const tabs = customTabs || defaultTabs;

  const handleChange = (index: number) => {
    const selectedTab = tabs[index];
    // Only allow change if the tab is not disabled
    if (!disabledTabs.includes(selectedTab.value)) {
      onChange(selectedTab.value);
    }
  };

  useLayoutEffect(() => {
    if (!tabs.some((tab) => tab.value === value)) {
      // Find the first non-disabled tab
      const firstEnabledTabIndex = tabs.findIndex(
        (tab) => !disabledTabs.includes(tab.value),
      );
      if (firstEnabledTabIndex !== -1) {
        handleChange(firstEnabledTabIndex);
      }
    }
  }, []);

  return (
    <Tabs
      index={tabs.findIndex((tab) => tab.value === value)}
      onChange={handleChange}
      isFitted={isFitted}
    >
      <TabList>
        {tabs.map(({ label, value: lang }) => {
          const isDisabled = disabledTabs.includes(lang);
          return (
            <LanguageTab
              isComplete={completedTabs.includes(lang)}
              isDisabled={isDisabled}
              disabledTooltip={isDisabled ? disabledTooltip : undefined}
              key={`lang_tab_${lang}`}
            >
              {label}
            </LanguageTab>
          );
        })}
      </TabList>
    </Tabs>
  );
};

export default LanguageTabs;
